import React, { useEffect, useState } from 'react';
import { useProductService } from '../services/product';
import { Product, ProductCategory } from '../services/product/types';
import { BounceLoader } from '../loader';

import { ProductCard } from './index';

const PAGE_SIZE = 12;

interface Props {
    categories: ProductCategory[];
}

const ProductGrid = ({ categories }: Props) => {
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [activeCategory, setActiveCategory] = useState<ProductCategory>(categories[0]);
    const [lastProductId, setLastProductId] = useState<string | undefined>();
    const [products, setProducts] = useState<Product[]>([]);
    const productService = useProductService();

    const totalPages = Math.ceil(products.length / PAGE_SIZE);
    const paginated = products.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const handleCategoryChange = (category: ProductCategory) => {
        setProducts([]);
        setLastProductId(undefined);
        setActiveCategory(category);
        setPage(0);
    };

    const loadMoreProducts = async () => {
        setIsLoading(true);
        setLastProductId(undefined);
        const data = await productService.searchProductList({
            categoryId: String(activeCategory.id),
            afterId: lastProductId
        });
        setProducts(prev => [...prev, ...data]);
        if (data.length) {
            setLastProductId(String(data[data.length - 1].id));
        }
        return data;
    };

    const navigateToPreviousPage = () => {
        setPage(p => p - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const navigateToNextPage = () => {
        if (lastProductId) {
            loadMoreProducts().finally(() => {
                setPage(p => p + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setIsLoading(false);
            });
        } else {
            setPage(p => p + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        loadMoreProducts().finally(() => setIsLoading(false));
    }, [activeCategory]);

    return (
        <>
            <div className="spp-filter-pills">
                {categories.map(category => (
                    <button
                        key={category.name}
                        className={`spp-filter-pill ${activeCategory.id === category.id ? 'spp-filter-pill--active' : ''}`}
                        onClick={() => handleCategoryChange(category)}
                    >
                        {category.name}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <BounceLoader title="Loading products..." subTitle={`For Category ${activeCategory.name}`} />
            ) : (
                <div className="spp-product-grid">
                    {paginated.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div className="store-pagination">
                    <button
                        className="tx-page-btn"
                        onClick={navigateToPreviousPage}
                        disabled={page === 0}
                    >
                        ←
                    </button>
                    <span className="tx-page-label">{page + 1} / {totalPages}</span>
                    <button
                        className="tx-page-btn"
                        onClick={navigateToNextPage}
                        disabled={page === totalPages - 1}
                    >
                        →
                    </button>
                </div>
            )}
        </>
    );
};

export default ProductGrid;